import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { MeterDetailModel } from 'src/app/models/maintenance/meter-detail.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ArticleSelectionTableModel } from 'src/app/models/tables/archive/article-selection-table.model';
import { MeterDetailCaptureTableModel } from 'src/app/models/tables/maintenance/meter-detail-capture-table.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MeterDetailService {

  private readonly _httpClient = inject(HttpClient);

  create(meterDetailContract: MeterDetailModel): Observable<MeterDetailModel> {
    return this._httpClient.post<MeterDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.meterDetail.CreateAsync}`,
      meterDetailContract
    );
  }

  createDetails(meterDetails: Array<MeterDetailModel>): Observable<boolean> {
    return this._httpClient.post<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.meterDetail.CreateDetailsAsync}`,
      meterDetails
    );
  }

  delete(meterDetailId: number): Observable<MeterDetailModel> {
    const params = new HttpParams().set('meterDetailId', meterDetailId);
    return this._httpClient.delete<MeterDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.meterDetail.DeleteAsync}`,
      { params: params }
    );
  }

  deleteSomeDetails(meterDetailIds: Array<number>): Observable<boolean> {
    return this._httpClient.put<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.meterDetail.DeleteSomeDetailsAsync}`,
      meterDetailIds
    );
  }

  exportDetailConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.meterDetail.ExportDetailConsultation}`, filters, {
      params,
      responseType: 'blob'
    });
  }

  getAllMeterDetailsWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.meterDetail.GetAllMeterDetailsWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  getArticlesForSelection(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<ArticleSelectionTableModel>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<ArticleSelectionTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.meterDetail.GetArticlesForSelectionAsync}`,
      filters, { params: params }
    );
  }

  getDetailsForCapture(page: number, limit: number, columnSort: string | null, ascendant: boolean, meterId: number): Observable<PagedResponseModel<MeterDetailCaptureTableModel>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant).set('meterId', meterId);
    return this._httpClient.get<PagedResponseModel<MeterDetailCaptureTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.meterDetail.GetDetailsForCaptureAsync}`,
      { params: params }
    );
  }

  getById(meterDetailId: number): Observable<MeterDetailModel> {
    const params = new HttpParams().set('meterDetailId', meterDetailId);
    return this._httpClient.get<MeterDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.meterDetail.GetByIdAsync}`,
      { params: params }
    );
  }

  getCalculatedQuantity(meterDetail: MeterDetailModel): Observable<number> {
    return this._httpClient.post<number>(`${environment.apiCervinoBackend}${enpointCervinoWeb.meterDetail.GetCalculatedQuantityAsync}`,
      meterDetail
    );
  }

  calculateResetHourMeter(meterDetail: MeterDetailModel): Observable<MeterDetailModel> {
    return this._httpClient.post<MeterDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.meterDetail.CalculateResetHourMeterAsync}`,
      meterDetail
    );
  }

  getColumnsArticlesForSelection(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.meterDetail.GetColumnsArticlesForSelectionAsync}`);
  }

  getColumnsForDetailConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.meterDetail.GetColumnsForDetailConsultationAsync}`);
  }

  getColumnsDetailsForCapture(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.meterDetail.GetColumnsDetailsForCaptureAsync}`);
  }

  getMeterDetailByKeys(meterId: number, articleId: number): Observable<MeterDetailModel> {
    const params = new HttpParams().set('meterId', meterId).set('articleId', articleId);
    return this._httpClient.get<MeterDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.meterDetail.GetMeterDetailByKeysAsync}`,
      { params: params }
    );
  }

  resetHourMeter(meterDetailId: number): Observable<MeterDetailModel> {
    const params = new HttpParams().set('meterDetailId', meterDetailId);
    return this._httpClient.put<MeterDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.meterDetail.ResetHourMeterAsync}`,
      null, { params: params }
    );
  }

  update(meterDetailContract: MeterDetailModel): Observable<MeterDetailModel> {
    return this._httpClient.put<MeterDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.meterDetail.UpdateAsync}`,
      meterDetailContract
    );
  }
}
